import { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { AdminImportCourse, AdminImportStudent, AdminImportTeacher } from "../rosterImportApiSlice";
import {
	formatStudentInfo,
	formatTeacherInfo,
	formatCourseInfo,
} from "../conflict/conflictReviewUtils";
import {
	buildItemsForCategory,
	categorizeError,
	ErrorCategoryKey,
	formatIssueForList,
	hasRegistrationLicenseBlurb,
	ImportEntity,
	PHASE_CATEGORIES,
	PhaseKey,
	stripRowPrefix,
} from "./importSummaryUtils";
import { CollapsibleItem } from "./CollapsibleItem";
import IssueCard from "./IssueCard";

interface PhaseIssuesPanelProps {
	phaseKey: PhaseKey;
	phaseTitle: string;
	errorMessages: string[];
	entitiesByRow: Map<number, ImportEntity>;
}

const PAGE_SIZE = 5;

export function PhaseIssuesPanel({
	phaseKey,
	phaseTitle,
	errorMessages,
	entitiesByRow,
}: PhaseIssuesPanelProps) {
	const categories = useMemo(() => PHASE_CATEGORIES[phaseKey], [phaseKey]);

	// #region Categorize the Errors
	// categorize the errors and put them into buckets for each category
	const errorsByCategory = useMemo(() => {
		const categoryBuckets: Partial<Record<ErrorCategoryKey, string[]>> = Object.fromEntries(
			categories.map((c) => [c.key, [] as string[]])
		);

		//place the errors into the correct bucket
		for (const message of errorMessages) {
			const key = categorizeError(phaseKey, message);
			const safeKey = categoryBuckets[key] ? key : "other";
			(categoryBuckets[safeKey] as string[]).push(message);
		}

		return categoryBuckets;
	}, [categories, errorMessages, phaseKey]);

	// number of errors for each category as a Record
	const categoryErrorCounts = useMemo(() => {
		return Object.fromEntries(
			categories.map((c) => [c.key, errorsByCategory[c.key]?.length ?? 0])
		) as Record<ErrorCategoryKey, number>;
	}, [categories, errorsByCategory]);
	// #endregion

	const [openCategory, setOpenCategory] = useState<ErrorCategoryKey | null>(null);

	const openCategoryErrors = openCategory ? (errorsByCategory[openCategory] ?? []) : [];

	const allItems = useMemo(() => {
		if (!openCategory) return [];
		return buildItemsForCategory(openCategoryErrors, entitiesByRow);
	}, [openCategory, openCategoryErrors, entitiesByRow]);

	// #region Pagination
	const [page, setPage] = useState(1);

	// reset page when changing phase summaries and categories in the PhaseIssuesPanel
	useEffect(() => {
		setPage(1);
	}, [phaseKey, openCategory]);

	const totalItemsCount = allItems.length;
	const totalPages = Math.max(1, Math.ceil(totalItemsCount / PAGE_SIZE));

	const start = (page - 1) * PAGE_SIZE;
	const end = Math.min(totalItemsCount, start + PAGE_SIZE);
	const pageItems = allItems.slice(start, end);
	// #endregion

	// #region Toggling Items for each Category
	const [openItemKeys, setOpenItemKeys] = useState<Set<string>>(() => new Set());

	const toggleItem = (itemKey: string) => {
		setOpenItemKeys((prev) => {
			const next = new Set(prev);
			prev.has(itemKey) ? next.delete(itemKey) : next.add(itemKey);
			return next;
		});
	};

	const openItemsForCategory = (itemKeys: string[]) => {
		setOpenItemKeys((prev) => new Set([...prev, ...itemKeys]));
	};

	const closeItemsForCategory = (itemKeys: string[]) => {
		const keysToRemove = new Set(itemKeys);
		setOpenItemKeys((prev) => new Set([...prev].filter((key) => !keysToRemove.has(key))));
	};

	// category-aware keys so open state doesn't collide across categories
	const pageItemKeys = pageItems.map((item) =>
		openCategory ? `${openCategory}|${item.key}` : item.key
	);

	const isItemOpen = (itemKey: string) => openItemKeys.has(itemKey);

	// Check if any items are currently open
	const anyItemOpen = useMemo(
		() => pageItemKeys.some((key) => isItemOpen(key)),
		[pageItemKeys, isItemOpen]
	);

	// Expand all items on the page by default when page/category changes
	useEffect(() => {
		if (pageItemKeys.length > 0) {
			openItemsForCategory(pageItemKeys);
		}
	}, [openCategory, page]);

	// #endregion

	const visibleCategories = useMemo(
		() => categories.filter((c) => (categoryErrorCounts[c.key] ?? 0) > 0),
		[categories, categoryErrorCounts]
	);

	// Open the first category by default when visibleCategories change
	useEffect(() => {
		if (visibleCategories.length > 0 && openCategory === null) {
			setOpenCategory(visibleCategories[0].key);
		}
	}, [visibleCategories, openCategory]);

	// Helper functions to determine entity type
	const isStudent = (entity: ImportEntity | undefined): entity is AdminImportStudent => {
		return entity != null && "studentId" in entity && "dateOfBirth" in entity;
	};

	const isTeacher = (entity: ImportEntity | undefined): entity is AdminImportTeacher => {
		return entity != null && "email" in entity && "phoneNumber" in entity;
	};

	const isCourse = (entity: ImportEntity | undefined): entity is AdminImportCourse => {
		return entity != null && "name" in entity && !("email" in entity);
	};

	const SECTION_TITLE = phaseTitle;
	const SECTION_DESCRIPTION =
		"Issues were found during the import! Click a category below to view all issues that occurred.";

	const showRegLicenseNote =
		openCategory === "registration" &&
		openCategoryErrors.some((m) => hasRegistrationLicenseBlurb(stripRowPrefix(m)));

	return (
		<div
			className="-mx-4 -my-3 px-4 py-3 bg-gray-100"
			onClick={(e) => e.stopPropagation()}
			onMouseDown={(e) => e.stopPropagation()}
			onKeyDown={(e) => e.stopPropagation()}>
			<h3>{SECTION_TITLE} Issues</h3>
			<p className="text-sm text-gray-600 mb-2">{SECTION_DESCRIPTION}</p>
			<div className="grid grid-cols-1 sm:grid-cols-4">
				{visibleCategories.map((c) => (
					<IssueCard
						key={`${phaseKey}-${c.key}`}
						title={c.label}
						issueCount={categoryErrorCounts[c.key]}
						isOpen={openCategory === c.key}
						onToggle={() => setOpenCategory(c.key)}
						ariaLabel={`Toggle ${c.label} issues`}
						titleAttr={`Click to view ${c.label.toLowerCase()} issues`}
					/>
				))}
			</div>

			{openCategory && pageItems.length > 0 && (
				<section className="rounded-b-lg border bg-white p-4">
					<div className="flex justify-between">
						<h3>
							{categories.find((c) => c.key === openCategory)?.label ?? ""} Issues
						</h3>
						<div className="mb-2 flex flex-wrap items-center justify-end gap-2">
							<button
								type="button"
								className="btn btn-action !text-xs text-gray-900"
								onClick={(e) => {
									e.stopPropagation();
									if (anyItemOpen) {
										closeItemsForCategory(pageItemKeys);
									} else {
										openItemsForCategory(pageItemKeys);
									}
								}}>
								<span className="flex items-center gap-2">
									<ChevronDownIcon
										className={`h-4 w-4 text-gray-900 transition-transform ${anyItemOpen ? "rotate-180" : ""}`}
										aria-hidden="true"
									/>
									{anyItemOpen ? "Collapse All" : "Expand All"}
								</span>
							</button>
						</div>
					</div>

					{showRegLicenseNote && (
						<div className="mb-2 rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">
							<p className="font-semibold">Note</p>
							<p>
								Students can't be registered in the course. Either the school
								license is no longer active or there are no available student
								licenses remaining.
							</p>
						</div>
					)}

					{totalItemsCount > 0 && totalItemsCount > PAGE_SIZE && (
						<div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
							<div className="text-xs text-gray-700">
								Showing <span className="font-medium">{start + 1}</span> to{" "}
								<span className="font-medium">{end}</span> of{" "}
								<span className="font-medium">{totalItemsCount}</span>
							</div>

							<div className="flex items-center gap-2">
								<button
									type="button"
									className="btn btn-neutral !px-2 !py-1 !text-xs"
									disabled={page <= 1}
									onClick={(e) => {
										e.stopPropagation();
										setPage((p) => Math.max(1, p - 1));
									}}>
									Prev
								</button>

								<div className="text-xs text-gray-700">
									Page <span className="font-medium">{page}</span> / {totalPages}
								</div>

								<button
									type="button"
									className="btn btn-neutral !px-2 !py-1 !text-xs"
									disabled={page >= totalPages}
									onClick={(e) => {
										e.stopPropagation();
										setPage((p) => Math.min(totalPages, p + 1));
									}}>
									Next
								</button>
							</div>
						</div>
					)}

					<div className="space-y-2">
						{pageItems.map((item) => {
							let title = "Unassigned";
							let subtitle = "These issues couldn't be matched to a specific row.";

							if (isStudent(item.entity)) {
								title = `${item.entity.lName}, ${item.entity.fName}`;
								subtitle = formatStudentInfo(item.entity, false);
							} else if (isTeacher(item.entity)) {
								title = `${item.entity.lName}, ${item.entity.fName}`;
								subtitle = formatTeacherInfo(item.entity, false);
							} else if (isCourse(item.entity)) {
								title = item.entity.name;
								subtitle = formatCourseInfo(item.entity, false);
							}

							const badgeRight =
								item.rowNum != null ? (
									<span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
										Row {item.rowNum}
									</span>
								) : null;

							const uniqueKey = `${openCategory}|${item.key}`;
							const open = isItemOpen(uniqueKey);

							return (
								<CollapsibleItem
									key={`${phaseKey}-${uniqueKey}`}
									title={title}
									subtitle={subtitle}
									badgeRight={badgeRight}
									count={item.messages.length}
									open={open}
									onToggle={() => toggleItem(uniqueKey)}>
									<ul className="list-disc space-y-1 pl-5">
										{item.messages.map((msg, i) => (
											<li
												key={`${phaseKey}-${uniqueKey}-${i}`}
												className="break-words text-sm text-red-700">
												{formatIssueForList(msg, openCategory)}
											</li>
										))}
									</ul>
								</CollapsibleItem>
							);
						})}
					</div>
				</section>
			)}
		</div>
	);
}
